// --- React & Redux
import React, { useCallback, useState } from 'react';
import { useSelector } from 'react-redux';
// --- External UI Libraries
import Banner from '@dtci/spellbook/Banner';
import Button from '@dtci/spellbook/Button';
import ContentBlock from '@dtci/spellbook/ContentBlock';
import DataGrid from '@dtci/spellbook/DataGrid';
import Typography from '@dtci/spellbook/Typography';
// --- API & Utils
import { datastageApi } from 'datastage/api';
import { startJobStatusTracker } from 'datastage/utils/jobStatusTracker';
import { usePermissions } from 'datastage/utils/permissions';
// --- Hooks
import { useAgGridApi } from 'datastage/components/AGGrid/hooks';
import { useLoading } from 'datastage/utils/hooks/useLoading';
import { useToastNotification } from 'datastage/components/ToastNotifications';
import { useToasts } from 'datastage/utils/hooks/useToasts';
import { useDeleteDialog } from 'datastage/components/Dialogs/dialogs/delete';
import { useDealVersionActions } from 'datastage/store/dealVersion/useDealVersion';
// --- Config
import * as columnDefs from 'datastage/components/AGGrid/config/columns-def';
// --- Components
import { DealStatusDrawer } from '../deal-status-drawer';
// --- Styles
import styles from 'datastage/components/AGGrid/details-table.module.scss';

// Helper to get unique dealIds from selected rows
const getUniqueDealIds = (rows) =>
    Array.from(new Set(rows.map((item) => item.dealVersionKey?.dealId).filter(Boolean)));

const getSnapshotDeleteLabel = (row) => {
    const dealId = row.dealVersionKey?.dealId;
    const version = row.dealVersionKey?.version;
    const subversion = row.dealVersionKey?.subversion;
    const snapshotDescription = row.snapshotDescription?.trim();

    if (snapshotDescription) {
        return snapshotDescription;
    }

    return `Deal ${dealId}, Version ${version}, Subversion ${subversion}`;
};

const buildSnapshotDeleteDialogMessages = (rows) => {
    if (rows.length === 1) {
        const row = rows[0];
        const dealId = row.dealVersionKey?.dealId;
        const version = row.dealVersionKey?.version;
        const snapshotDescription = row.snapshotDescription || '';

        return [
            `Are you sure you wish to delete this snapshot for Deal ${dealId}, Version ${version}?`,
            snapshotDescription
                ? `Snapshot: ${snapshotDescription}. This action cannot be reversed.`
                : 'Please note: This action cannot be reversed.',
        ];
    }

    const uniqueDealIds = getUniqueDealIds(rows);
    const dealLabel =
        uniqueDealIds.length === 1 ? `Deal ${uniqueDealIds[0]}` : `${uniqueDealIds.length} deals`;
    const snapshotList = rows.map((row) => getSnapshotDeleteLabel(row)).join('; ');

    return [
        `Are you sure you wish to delete ${rows.length} snapshots for ${dealLabel}?`,
        `${snapshotList}. This action cannot be reversed.`,
    ];
};

/**
 * Deal Version Grid
 *
 * * Features:
 * - Displays deal versions in a data grid
 * - Allows downloading PDFs and XLSX files for deal versions
 * - Supports comparing multiple deal versions
 * - Shows banners based on selection state
 * - Integrates with Redux for deal version state
 * - Uses custom hooks for grid API and loading state
 *
 * @returns {Element}
 * @constructor
 */
export const DealVersionGrid = () => {
    const agGridApi = useAgGridApi();
    const { setLoadingTrue, setLoadingFalse } = useLoading();
    const { showErrorToast, showSuccessToast } = useToasts();
    const { openDeleteDialog } = useDeleteDialog();
    const { isDealVersioningReadOnly } = usePermissions();
    const { updateDealVersion } = useDealVersionActions();
    const { dealVersions } = useSelector((s) => s.dealVersion);
    const { openToast } = useToastNotification();
    const [banner, setBanner] = useState(null);
    const [selectedRows, setSelectedRows] = useState([]);

    const showSpinner = useSelector((s) => s.spinner.showSpinner);

    // Download PDF for selected deal version
    const downloadPDF = async (data) => {
        setLoadingTrue();
        try {
            const exists = await datastageApi.checkPDFExists(data);
            if (exists) {
                const res = await datastageApi.downloadPDF(data);
                if (res?.code) {
                    showErrorToast(res?.message || 'Failed to download PDF.');
                }
                setLoadingFalse();
                return;
            }

            const res = await datastageApi.createPDF(data);
            if (res?.code) {
                showErrorToast(res?.message || 'Failed to create PDF.');
                setLoadingFalse();
                return;
            }

            startJobStatusTracker(res?.id, async (message, status) => {
                setLoadingTrue();
                openToast(message, status);
                if (status === 'success') {
                    await datastageApi.downloadPDF(data);
                }
                setLoadingFalse();
            });
        } catch (error) {
            showErrorToast(error?.message || 'Error downloading PDF.');
            console.error('Error downloading pdf:', error);
        } finally {
            setLoadingFalse();
        }
    };

    // Download XLSX for selected deal version
    const downloadXLSX = async (data) => {
        setLoadingTrue();
        try {
            const exists = await datastageApi.checkXLSXExists(data);
            if (exists) {
                const res = await datastageApi.downloadXLSX(data);
                if (res?.code) {
                    showErrorToast(res?.message || 'Failed to download XLSX.');
                }
                setLoadingFalse();
                return;
            }

            const res = await datastageApi.createXLSX(data);
            if (res?.code) {
                showErrorToast(res?.message || 'Failed to create XLSX.');
                setLoadingFalse();
                return;
            }
            
            startJobStatusTracker(res?.id, async (message, status) => {
                setLoadingTrue();
                openToast(message, status);
                if (status === 'success') {
                    await datastageApi.downloadXLSX(data);
                }
                setLoadingFalse();
            });
        } catch (error) {
            showErrorToast(error?.message || 'Error downloading XLSX.');
            console.error('Error downloading xlsx:', error);
            setLoadingFalse();
        } finally {
            setLoadingFalse();
        }
    };

    // Handle selection changes in the grid
    const onSelectionChanged = useCallback((event) => {
        const selected = event.api.getSelectedRows();
        setSelectedRows(selected);
        const dealIds = selected.map((item) => item.dealVersionKey?.dealId).filter(Boolean);
        if (selected.length > 0) {
            const uniqueDealIds = Array.from(new Set(dealIds));
            if (uniqueDealIds.length > 1) {
                setBanner(
                    'Deal Ids must match in your selection in order to compare deal versions.',
                );
            } else {
                setBanner(
                    selected.length === 1
                        ? '1 item selected. Please select additional item(s) for comparison.'
                        : `${selected.length} items selected`,
                );
            }
        } else {
            setBanner(null);
        }
    }, []);

    // Handle deal version XLSX comparison download
    const handleDealVersionComparison = async () => {
        setLoadingTrue();
        try {
            const ids = { idsToCompare: selectedRows.map((row) => row.id) };
            const res = await datastageApi.checkComparisonExists(ids);

            if (res.exists) {
                const downloadRes = await datastageApi.downloadDealComparison(ids, res?.fileName);
                if (downloadRes?.code) {
                    showErrorToast(downloadRes?.message || 'Failed to download deal comparison.');
                }
                setLoadingFalse();
                return;
            }

            const createRes = await datastageApi.createDealComparison(ids, res?.fileName);
            if (createRes?.code) {
                showErrorToast(createRes?.message || 'Failed to create deal comparison.');
                setLoadingFalse();
                return;
            }

            startJobStatusTracker(createRes?.id, async (message, status, data) => {
                setLoadingTrue();
                openToast(message, status);
                if (status === 'success') {
                    await datastageApi.downloadDealComparison(ids, res?.fileName);
                }
                setLoadingFalse();
            });
        } catch (error) {
            showErrorToast(error?.message || 'Error downloading deal comparison.');
            console.error('Error downloading deal comparison:', error);
            setLoadingFalse();
        } finally {
            setLoadingFalse();
        }

    };

    // Handle deal version PDF comparison download (exactly 2 items)
    const handlePdfComparison = async () => {
        if (selectedRows.length !== 2) return;

        setLoadingTrue();
        try {
            const id1 = selectedRows[0].id;
            const id2 = selectedRows[1].id;
            const res = await datastageApi.checkPdfComparisonExists(id1, id2);

            if (res?.error) {
                showErrorToast(res?.error?.message || 'Error checking PDF comparison.');
                setLoadingFalse();
                return;
            }

            if (res?.exists) {
                const downloadRes = await datastageApi.downloadPdfComparison(id1, id2, res?.fileName);
                if (downloadRes?.code) {
                    showErrorToast(downloadRes?.message || 'Failed to download PDF comparison.');
                }
                return;
            }

            const createRes = await datastageApi.createPdfComparison(id1, id2, res?.fileName);
            if (createRes?.code || createRes?.error) {
                showErrorToast(createRes?.message || createRes?.error?.message || 'Failed to create PDF comparison.');
                setLoadingFalse();
                return;
            }

            startJobStatusTracker(createRes?.id, async (message, status) => {
                setLoadingTrue();
                openToast(message, status);
                if (status === 'success') {
                    await datastageApi.downloadPdfComparison(id1, id2, res?.fileName);
                }
                setLoadingFalse();
            });
        } catch (error) {
            showErrorToast(error?.message || 'Error downloading PDF comparison.');
            console.error('Error downloading PDF comparison:', error);
            setLoadingFalse();
        } finally {
            setLoadingFalse();
        }
    };

    // Handle closing the banner
    const handleClose = useCallback(() => {
        agGridApi.AgGridApiRef.current?.api?.deselectAll();
        setBanner(null);
        setSelectedRows([]);
    }, [agGridApi.AgGridApiRef]);

    const deleteSnapshots = useCallback(
        async (rows) => {
            setLoadingTrue();
            let deletedCount = 0;
            let failedCount = 0;

            try {
                for (const row of rows) {
                    const dealId = row.dealVersionKey?.dealId;
                    const version = row.dealVersionKey?.version;
                    const subversion = row.dealVersionKey?.subversion;

                    try {
                        const res = await datastageApi.deleteDealSnapshot(
                            dealId,
                            version,
                            subversion,
                        );

                        if (res?.code || res?.error) {
                            failedCount += 1;
                            showErrorToast(
                                res?.message ||
                                    res?.error?.message ||
                                    `Failed to delete snapshot for Deal ${dealId}, Version ${version}.`,
                            );
                            continue;
                        }

                        deletedCount += 1;
                    } catch (error) {
                        failedCount += 1;
                        showErrorToast(error?.message || 'Error deleting snapshot.');
                        console.error('Error deleting snapshot:', error);
                    }
                }

                if (deletedCount > 0) {
                    showSuccessToast(
                        deletedCount === 1
                            ? 'Snapshot deleted successfully.'
                            : `${deletedCount} snapshots deleted successfully.`,
                    );
                    handleClose();
                    updateDealVersion({ refreshDealVersion: true });
                }
            } finally {
                setLoadingFalse();
            }
        },
        [
            handleClose,
            setLoadingTrue,
            setLoadingFalse,
            showErrorToast,
            showSuccessToast,
            updateDealVersion,
        ],
    );

    const confirmDeleteSnapshots = useCallback(
        (rows) => {
            openDeleteDialog('Yes, Delete', buildSnapshotDeleteDialogMessages(rows), () =>
                deleteSnapshots(rows),
            );
        },
        [openDeleteDialog, deleteSnapshots],
    );

    const handleDeleteSnapshot = useCallback(
        (row) => {
            confirmDeleteSnapshots([row]);
        },
        [confirmDeleteSnapshots],
    );

    const handleDeleteSelectedSnapshots = useCallback(() => {
        confirmDeleteSnapshots(selectedRows);
    }, [confirmDeleteSnapshots, selectedRows]);

    const uniqueDealIds = getUniqueDealIds(selectedRows);
    const showCompareButton = selectedRows.length > 1 && uniqueDealIds.length === 1;
    const showPdfCompareButton = selectedRows.length === 2 && uniqueDealIds.length === 1;
    const showDeleteSelectedButton =
        !isDealVersioningReadOnly &&
        selectedRows.length > 0 &&
        selectedRows.every((row) => row.canDelete === true);
    const isWarning = selectedRows.length === 1 || uniqueDealIds.length > 1;

    return (
        <>
            {banner && (
                <Banner
                    faPackage="far"
                    iconTypePrimary={isWarning ? 'fa-times-circle' : 'fa-check-circle'}
                    role="status"
                    type={isWarning ? 'warning' : 'success'}
                    onClose={handleClose}
                    style={{
                        width: '100%',
                        minHeight: 48,
                        display: 'flex',
                        alignItems: 'center',
                        marginBottom: 12,
                        padding: '12px 20px',
                        boxSizing: 'border-box',
                    }}
                >
                    <ContentBlock
                        className="banner-content"
                        style={{
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'flex-start',
                            gap: 12,
                            width: '100%',
                            minHeight: 48,
                        }}
                    >
                        <Typography className="main-banner-text" variant="T20">
                            {banner}
                        </Typography>
                        {false && showPdfCompareButton && (
                            <Button
                                style={{ color: '#3611c8' }}
                                iconType=""
                                type="button"
                                styleType="link"
                                onClick={handlePdfComparison}
                            >
                                COMPARE DEAL VERSIONS PDF
                            </Button>
                        )}
                        {showCompareButton && (
                            <Button
                                style={{ color: '#3611c8' }}
                                iconType=""
                                type="button"
                                styleType="link"
                                onClick={handleDealVersionComparison}
                            >
                                COMPARE DEAL VERSIONS XLSX
                            </Button>
                        )}
                        {showDeleteSelectedButton && (
                            <Button
                                style={{ color: '#3611c8' }}
                                iconType=""
                                type="button"
                                styleType="link"
                                onClick={handleDeleteSelectedSnapshots}
                            >
                                DELETE SNAPSHOT{selectedRows.length === 1 ? '' : 'S'}
                            </Button>
                        )}
                    </ContentBlock>
                </Banner>
            )}
            <DataGrid
                className={styles.dataGrid}
                autoSizing
                agGrid={{
                    ref: agGridApi.AgGridApiRef,
                    rowData: dealVersions,
                    headerHeight: 45,
                    onSelectionChanged,
                    defaultColDef: {
                        wrapText: true,
                        autoHeight: true,
                        suppressMenu: true,
                        resizable: true,
                        cellStyle: { 
                            paddingTop: '10px', 
                            paddingBottom: '10px',
                            wordBreak: 'normal',
                            overflowWrap: 'break-word',
                        },
                    },
                }}
                bordered="none"
                selectable
                columnDefs={
                    columnDefs['dealVersions'](
                        downloadPDF,
                        downloadXLSX,
                        handleDeleteSnapshot,
                        isDealVersioningReadOnly,
                    )['default']
                }
                sortable
                striped
                noResultsMessage={showSpinner ? 'Loading...' : 'No results found'}
            />
            <DealStatusDrawer />
        </>
    );
};
